// Looking back over a finished game: the game at any point, from its starting state and its commits as the server keeps
// them (see the API's GET /games/:id/history), replayed the way the authority played them.
// A position is the game after that many moves (0: as it began; Length: as it ended). A turn begins where the current
// player or the round changes (and at 0), so a turn's moves include the decisions others made during it.
const GameState = require('./entities/GameState');
const Rules = require('./Rules');
const { MoveTypes } = require('./Moves/MoveTypes');
const { Back } = require('./Moves/Decisions');

const clone = (data) => JSON.parse(JSON.stringify(data));

class Review
{
    // initial: the game's starting state (GameStateData); commits: [{ player, moves, at }], in order
    constructor(initial, commits)
    {
        // Every move, in order: { move, player (number), name, description (a line each, see Rules.Step), round, age, at,
        // takenBack (a choice taken back to choose again), back (the going back: nothing to show) }
        this.moves = [];
        // Where each turn begins: { start (position), player (number), name, round, age }
        this.turns = [];
        // The game as each turn began, and as it ended, by position (to replay a turn's moves from)
        this.snapshots = new Map();
        // The first move the rules no longer accept, if any (the rules changed since): the review stops before it
        this.error = null;
        let state = new GameState(clone(initial));
        const begin = (position) => {
            this.turns.push({ start: position, player: state.currentPlayer, name: state.GetCurrentPlayerState().name, round: state.round, age: state.age });
            this.snapshots.set(position, state.toData());
        };
        begin(0);
        for (const commit of commits)
        {
            for (const move of commit.moves)
            {
                const error = Rules.Validate(state, move);
                if (error !== null)
                {
                    this.error = `Move ${this.moves.length + 1} (${move.type}) no longer replays: ${error}`;
                    break;
                }
                const before = state;
                const { after, description } = Rules.Step(before, move);
                const decision = move.type === MoveTypes.Decide ? before.decisions.find(d => d.id === move.decisionId) : null;
                const back = !!decision && move.value === Back && !!decision.back;
                if (back)
                {
                    this.moves.filter(entry => entry.decisionId === decision.back.id).forEach(entry => { entry.takenBack = true; });
                }
                this.moves.push({ move, player: move.player, name: before.playerStates[move.player].name, description, round: before.round, age: before.age,
                    ...(commit.at ? { at: commit.at } : {}), ...(decision ? { decisionId: decision.id } : {}), ...(back ? { back } : {}) });
                state = after;
                if (after.currentPlayer !== before.currentPlayer || after.round !== before.round)
                {
                    begin(this.moves.length);
                }
            }
            if (this.error)
            {
                break;
            }
        }
        this.snapshots.set(this.moves.length, state.toData());
        // (a game that ended: no turn begins after its last move, which ends the last turn played)
        if (state.finished && this.turns.length > 1 && this.turns.at(-1).start === this.moves.length)
        {
            this.turns.pop();
        }
    }

    get Length()
    {
        return this.moves.length;
    }

    // The turn a position is in (its index in turns): the last to begin at or before it
    TurnAt(position)
    {
        let index = 0;
        this.turns.forEach((turn, i) => { if (turn.start <= position) index = i; });
        return index;
    }

    // The positions a turn covers: from its start to the next turn's (or the end)
    TurnSpan(index)
    {
        return { start: this.turns[index].start, end: this.turns[index + 1]?.start ?? this.Length };
    }

    // The journal up to a position, as the authority keeps it ({ player (name), age, round, description, at }): the moves
    // made by then, but for choices taken back to choose again
    JournalAt(position)
    {
        return this.moves.slice(0, position).filter(entry => !entry.back && !entry.takenBack)
            .map(entry => ({ player: entry.name, age: entry.age, round: entry.round, description: entry.description, ...(entry.at ? { at: entry.at } : {}) }));
    }

    // The game at a position (GameStateData), replayed from the start of its turn
    StateAt(position)
    {
        const at = Math.max(0, Math.min(this.Length, position));
        if (this.snapshots.has(at))
        {
            return clone(this.snapshots.get(at));
        }
        const start = this.turns[this.TurnAt(at)].start;
        let state = new GameState(clone(this.snapshots.get(start)));
        this.moves.slice(start, at).forEach(entry => { state = Rules.Step(state, entry.move).after; });
        return state.toData();
    }
}

module.exports = Review;
